// src/cockpit4/components/LeadCapturePanel.tsx
// ============================================
// P0 补充：官网留资查看面板
//   数据源：官网 lead.js 浮窗 → POST os.xshetech.com/lead-api/submit → vault/XEAOS/leads/leads.jsonl
//   本面板：GET /lead-api/list（仅 super_admin）→ 表格展示 时间/姓名/电话/公司/需求/来源
// ============================================

import { useEffect, useState, useCallback } from "react";
import { RefreshCw, Phone, Users, ClipboardCopy, CheckCircle2 } from "lucide-react";

interface LeadItem {
  name: string;
  phone: string;
  company: string;
  need: string;
  source: string;
  time: string;
}

export default function LeadCapturePanel() {
  const [leads, setLeads] = useState<LeadItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [copied, setCopied] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const resp = await fetch("/lead-api/list");
      const data = await resp.json();
      if (data?.success) {
        setLeads(Array.isArray(data.leads) ? data.leads : []);
      } else {
        setError(data?.error || "读取失败");
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const copyAll = async () => {
    const text = leads.map((l) => `${l.time} | ${l.name} | ${l.phone} | ${l.company} | ${l.need} | ${l.source}`).join("\n");
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch { /* ignore */ }
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2 text-[10px] font-mono text-gray-500">
        <Phone size={11} className="text-emerald-400" />
        <span>官网留资线索：来自 <span className="text-emerald-300">www.xshetech.com</span> 右下角「免费咨询 / 预约演示」浮窗</span>
        <button
          onClick={load}
          disabled={loading}
          className="ml-auto flex items-center gap-1 px-2 py-1 rounded text-[10px] font-mono text-gray-400 hover:text-white border border-white/[0.08]"
        >
          <RefreshCw size={9} className={loading ? "animate-spin" : ""} />
          刷新
        </button>
        <button
          onClick={copyAll}
          className="flex items-center gap-1 px-2 py-1 rounded text-[10px] font-mono text-cyan-300 hover:text-cyan-200 border border-cyan-500/30"
        >
          {copied ? <CheckCircle2 size={9} /> : <ClipboardCopy size={9} />}
          {copied ? "已复制" : "复制全部"}
        </button>
      </div>

      {error && <p className="text-[11px] text-red-400">{error}</p>}

      {loading ? (
        <div className="text-[12px] text-gray-500 py-6 text-center">加载中…</div>
      ) : leads.length === 0 ? (
        <div className="rounded-lg border border-dashed border-white/[0.08] p-6 text-center">
          <Users size={16} className="text-gray-600 mx-auto mb-2" />
          <p className="text-[11px] text-gray-600">暂无留资记录。官网访客提交后会自动出现在这里。</p>
        </div>
      ) : (
        <div className="space-y-2">
          <div className="text-[10px] font-mono text-gray-500">
            共 <span className="text-white">{leads.length}</span> 条留资
          </div>
          {leads.map((l, i) => (
            <div key={i} className="rounded-lg border border-white/[0.05] bg-white/[0.02] p-2.5">
              <div className="flex items-center gap-2 mb-1.5">
                <span className="text-[12px] font-bold text-white">{l.name}</span>
                <a
                  href={`tel:${l.phone}`}
                  className="text-[11px] text-cyan-300 hover:underline"
                >
                  {l.phone}
                </a>
                {l.company && <span className="text-[10px] text-gray-400">· {l.company}</span>}
                <span className="ml-auto text-[9px] font-mono text-gray-600">{l.time}</span>
              </div>
              {l.need && (
                <p className="text-[10px] text-gray-400 leading-relaxed">需求：{l.need}</p>
              )}
              {l.source && (
                <p className="text-[8px] font-mono text-gray-600 mt-1">来源：{l.source}</p>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
